import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, UsersIcon, ZapIcon, MessageSquareIcon, CreditCardIcon, Share2Icon, CalendarDaysIcon, PlugIcon, DatabaseIcon, CloudIcon, BrainIcon, WalletIcon, FileTextIcon, MapPinIcon, BarChart3Icon, BellIcon, PieChartIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { provenIntro, primaryResults, scaleStats, infrastructure, platformInAction, provenConclusion, type Bilingual } from '../data/provenContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const DOT_FIELD: React.CSSProperties = {
  backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)',
  backgroundSize: '18px 18px, 26px 26px',
  backgroundPosition: '0 0, 9px 13px'
};
const RESULT_ICONS = {
  people: UsersIcon,
  bolt: ZapIcon
};
const SCALE_ICONS = {
  users: UsersIcon,
  message: MessageSquareIcon,
  card: CreditCardIcon,
  share: Share2Icon,
  calendar: CalendarDaysIcon
};
const INFRA_ICONS = {
  plug: PlugIcon,
  database: DatabaseIcon,
  cloud: CloudIcon,
  brain: BrainIcon
};
const CITIZEN_ICONS = {
  card: CreditCardIcon,
  payment: WalletIcon,
  requests: FileTextIcon,
  message: MessageSquareIcon
};
const ORG_ICONS = {
  services: MapPinIcon,
  chart: BarChart3Icon,
  bell: BellIcon,
  report: PieChartIcon
};
function IconBadge({
  Icon,
  size = 'md'
}: {
  Icon: typeof UsersIcon;
  size?: 'sm' | 'md';
}) {
  const box = size === 'sm' ? 'w-9 h-9' : 'w-11 h-11';
  const glyph = size === 'sm' ? 'w-4 h-4' : 'w-[1.15rem] h-[1.15rem]';
  return <span className={`${box} rounded-full bg-[#4F46E5]/[0.07] flex items-center justify-center`} data-id="element-5699" data-mp="1978mn1bf1ln9">
      <Icon className={`${glyph} text-[#4F46E5]`} strokeWidth={1.75} aria-hidden="true" data-id="element-5700" data-mp="3ft4v67e9j558" />
    </span>;
}
export function Proven() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <section id="proof" className="bg-white border-t border-[#E4E6EC] py-16 lg:py-20 px-4 sm:px-6" data-id="element-5701" data-mp="322025o5ehb3v">
      <div className="max-w-[1180px] mx-auto" data-id="element-5702" data-mp="2mwf46cuxdxd">
        {/* Header */}
        <motion.div {...reveal} className="max-w-3xl mx-auto text-center" data-id="element-5703" data-mp="3uqffm68vvld3">
          <div className="flex items-center justify-center gap-3" data-id="element-5704" data-mp="3uob1ywl6n7zm">
            <span className="w-6 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-5705" data-mp="3pq9g63wdgukg" />
            <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" data-id="element-5706" data-mp="3ksr8shy06nek">
              {pick(provenIntro.eyebrow)}
            </p>
            <span className="w-6 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-5707" data-mp="2ea2u8852n381" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.14] tracking-tight text-[#121212] mt-4" data-id="element-5708" data-mp="29f5kt03vqrum">
            <span className="block" data-id="element-5709" data-mp="1z7b26xdtqziu">{pick(provenIntro.headlineLead)}</span>
            <span className="block" data-id="element-5710" data-mp="248qlol0csxz4">
              {pick(provenIntro.headlineBefore)}
              <span className="text-[#4F46E5]" data-id="element-5711" data-mp="296ju7kp4q42k">
                {pick(provenIntro.headlineAccent)}
              </span>
            </span>
          </h2>
          <p className="font-sans text-base text-[#5B6478] leading-relaxed mt-4" data-id="element-5712" data-mp="2hznby56t5330">
            {pick(provenIntro.intro)}
          </p>
        </motion.div>

        {/* Primary results */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10" data-id="element-5713" data-mp="2n0jikqijn3c4">
          {primaryResults.map((result, index) => {
          const Icon = RESULT_ICONS[result.icon];
          return <motion.article key={result.id} {...reveal} transition={{
            duration: 0.6,
            delay: index * 0.06,
            ease: EASE
          }} className="rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] px-6 py-7 text-center" data-id="element-5714" data-mp="2wujtj90ib2sr" data-mpi={index}>
                <div className="flex justify-center" data-id="element-5715" data-mp="2o15rvs22vjw8">
                  <IconBadge Icon={Icon} data-id="element-5716" data-mp="2o0mbwgvzn5ku" />
                </div>
                <div className="font-serif text-5xl lg:text-[3.5rem] text-[#121212] leading-none mt-4" data-id="element-5717" data-mp="320x0fd9ts82u">
                  {pick(result.value)}
                </div>
                <p className="font-sans text-base text-[#121212] mt-4" data-id="element-5718" data-mp="29bzmtngk6zgf">
                  {pick(result.label)}
                </p>
                <p className="font-sans text-sm text-[#5B6478] mt-1" data-id="element-5719" data-mp="25b7dku7osuk5">
                  {pick(result.detail)}
                </p>
                <p className="font-sans text-xs text-[#9AA0B0] mt-3" data-id="element-5720" data-mp="2j3dq86bmsjeu">
                  {pick(result.source)}
                </p>
              </motion.article>;
        })}
        </div>

        {/* Production scale strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4" data-id="element-5721" data-mp="1lmezythabi1v">
          {scaleStats.map((stat, index) => {
          const Icon = SCALE_ICONS[stat.icon];
          return <motion.div key={stat.id} {...reveal} transition={{
            duration: 0.6,
            delay: index * 0.05,
            ease: EASE
          }} className="rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.04)] px-4 py-5 text-center" data-id="element-5722" data-mp="5v33qjba9oce" data-mpi={index}>
                <div className="flex justify-center" data-id="element-5723" data-mp="kru52y4ipa4n">
                  <IconBadge Icon={Icon} size="sm" data-id="element-5724" data-mp="kq98l2yn7h45" />
                </div>
                <div className="font-serif text-2xl text-[#121212] leading-none mt-3" data-id="element-5725" data-mp="osntdslvgqy2">
                  {pick(stat.value)}
                </div>
                <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1.5" data-id="element-5726" data-mp="ftag0oumni82">
                  {pick(stat.label)}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Infrastructure panel */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.08,
        ease: EASE
      }} className="relative overflow-hidden rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-10 lg:px-12 mt-4" data-id="element-5727" data-mp="30kj7mihs2wxs">
          <span className="pointer-events-none absolute -right-16 -top-16 w-72 h-72 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-5728" data-mp="2cgldi3ompkyv" />
          <div className="relative text-center max-w-2xl mx-auto" data-id="element-5729" data-mp="22nw3ld8q8f5n">
            <p className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase" data-id="element-5730" data-mp="1xrouxcoy4pk0">
              {pick(infrastructure.eyebrow)}
            </p>
            <h3 className="font-serif text-2xl md:text-[1.9rem] text-[#121212] leading-snug mt-3" data-id="element-5731" data-mp="1opxv2oh37cy2">
              {pick(infrastructure.headline)}
            </h3>
          </div>

          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-6 mt-9 lg:divide-x lg:divide-[#4F46E5]/15" data-id="element-5732" data-mp="1jth4zoqbuoic">
            {infrastructure.points.map(point => {
            const Icon = INFRA_ICONS[point.icon];
            return <div key={point.id} className="text-center px-2" data-id="element-5733" data-mp="3lnn1mkel9c6s">
                  <div className="flex justify-center" data-id="element-5734" data-mp="3qnhq6haw5poo">
                    <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center" data-id="element-5735" data-mp="3qlww0ssho8rc">
                      <Icon className="w-[1.15rem] h-[1.15rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-5736" data-mp="3vf9hiu1vfzfv" />
                    </span>
                  </div>
                  <div className="font-serif text-3xl text-[#121212] leading-none mt-3" data-id="element-5737" data-mp="37o3vty992cxn">
                    {pick(point.value)}
                  </div>
                  <p className="font-sans text-sm text-[#5B6478] leading-snug mt-2" data-id="element-5738" data-mp="3cqcp6vq1bh0a">
                    {pick(point.label)}
                  </p>
                  {pick(point.detail) && <p className="font-sans text-xs text-[#9AA0B0] leading-snug mt-1" data-id="element-5739" data-mp="rztzp6n94lk6">
                      {pick(point.detail)}
                    </p>}
                </div>;
          })}
          </div>
        </motion.div>

        {/* Platform in action */}
        <motion.p {...reveal} transition={{
        duration: 0.6,
        delay: 0.05,
        ease: EASE
      }} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase text-center mt-12" data-id="element-5740" data-mp="1czp3rz1d3yf4">
          {pick(platformInAction.eyebrow)}
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.68fr)_minmax(0,1fr)] gap-4 mt-6 items-stretch" data-id="element-5741" data-mp="20pjh0hplfozw">
          {/* Citizens */}
          <motion.div {...reveal} transition={{
          duration: 0.6,
          delay: 0.06,
          ease: EASE
        }} className="rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] p-6 flex gap-5" data-id="element-5742" data-mp="2aptum3fjiyfl">
            <div className="flex-1 min-w-0" data-id="element-5743" data-mp="gdn7c69dgr6x">
              <p className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#5B6478]" data-id="element-5744" data-mp="bczkzxw38yj1">
                {pick(platformInAction.citizens.label)}
              </p>
              <h3 className="font-serif text-2xl text-[#121212] leading-[1.18] mt-3" data-id="element-5745" data-mp="2rymot7vbl125">
                <span className="block" data-id="element-5746" data-mp="2d9zxs8wbykv3">
                  {pick(platformInAction.citizens.headlineLead)}
                </span>
                <span className="block" data-id="element-5747" data-mp="2i8b7mjmmkvdb">
                  {pick(platformInAction.citizens.headlineAccent)}
                </span>
              </h3>
              <ul className="mt-6 space-y-3" data-id="element-5748" data-mp="24bej4otzbpzl">
                {platformInAction.citizens.items.map(item => {
                const Icon = CITIZEN_ICONS[item.icon];
                return <li key={item.text.en} className="flex items-center gap-3" data-id="element-5749" data-mp="1ud8ieajwspwb">
                      <IconBadge Icon={Icon} size="sm" data-id="element-5750" data-mp="1zaqsi49nxb5w" />
                      <span className="font-sans text-sm text-[#121212]/80" data-id="element-5751" data-mp="3oon9auonovdn">
                        {pick(item.text)}
                      </span>
                    </li>;
              })}
              </ul>
            </div>
            <img src={isFR ? 'https://cdn.magicpatterns.com/uploads/6ssDHDMHuDJ5EX6wAkS5z5/Capture_d’ecran,_le_2026-06-22_a_08.42.58.png' : 'https://cdn.magicpatterns.com/uploads/1pVBWf7FVvPhsQSXbKthD2/image.png'} alt={isFR ? 'Application citoyenne BCITI — carte numérique et services' : 'BCITI resident app — Digital Card and services'} className="hidden sm:block w-[38%] max-w-[190px] h-auto self-center object-contain rounded-xl border border-[#E4E6EC] shadow-[0_6px_20px_rgba(18,18,18,0.08)]" data-id="element-5752" data-mp="nqyfnmck306v" />
          </motion.div>

          {/* Core platform */}
          <motion.div {...reveal} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: EASE
        }} className="relative rounded-2xl px-5 py-8 text-center flex flex-col justify-center" data-id="element-5753" data-mp="3i8thsvskgf8f">
            <div className="flex items-center justify-center" data-id="element-5754" data-mp="3fncceuxzbt3x">
              <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-9 w-auto object-contain" data-id="element-5755" data-mp="3fpyewg60jifa" />
              <span className="mx-3 text-[#121212]/25 font-sans text-2xl font-light select-none" aria-hidden="true" data-id="element-5756" data-mp="3jpyyef7yyjt7">
                |
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[1.9rem] w-auto object-contain" data-id="element-5757" data-mp="2rz2tq1w2blh0" />
            </div>

            <ul className="mt-6 space-y-1.5" data-id="element-5758" data-mp="30xob3tpssug7">
              {platformInAction.coreLayers.map(layer => <li key={layer.en} className="font-sans text-sm text-[#121212]/75" data-id="element-5759" data-mp="2d7tzffh7tbxj">
                  {pick(layer)}
                </li>)}
            </ul>

            <div className="hidden lg:flex items-center justify-between mt-7" aria-hidden="true" data-id="element-5760" data-mp="c6dzptjgkt7e">
              <span className="w-9 h-9 rounded-full bg-white border border-[#E4E6EC] shadow-[0_1px_3px_rgba(18,18,18,0.08)] flex items-center justify-center" data-id="element-5761" data-mp="h4fei29uq6d2">
                <ArrowLeftIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-5762" data-mp="qva5tl1yfw5d" />
              </span>
              <span className="w-9 h-9 rounded-full bg-white border border-[#E4E6EC] shadow-[0_1px_3px_rgba(18,18,18,0.08)] flex items-center justify-center" data-id="element-5763" data-mp="3ud0oleuahyec">
                <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-5764" data-mp="3561tckutudu" />
              </span>
            </div>

            <p className="font-serif text-lg text-[#121212] leading-snug mt-7" data-id="element-5765" data-mp="1ekunm0mdgnd2">
              <span className="block" data-id="element-5766" data-mp="1imyb5nmbfiep">
                {pick(platformInAction.coreCaptionLead)}
              </span>
              <span className="block" data-id="element-5767" data-mp="334gxk22h94oj">
                {pick(platformInAction.coreCaptionAccent)}
              </span>
            </p>
          </motion.div>

          {/* Organizations */}
          <motion.div {...reveal} transition={{
          duration: 0.6,
          delay: 0.14,
          ease: EASE
        }} className="rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] p-6 flex gap-5 overflow-hidden" data-id="element-5768" data-mp="2ucf1efzfyak2">
            <div className="flex-1 min-w-0" data-id="element-5769" data-mp="2kco8giqj43m3">
              <p className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#5B6478]" data-id="element-5770" data-mp="2fewbvbexvtbk">
                {pick(platformInAction.organizations.label)}
              </p>
              <h3 className="font-serif text-2xl text-[#121212] leading-[1.18] mt-3" data-id="element-5771" data-mp="26g1969b3t1d0">
                <span className="block" data-id="element-5772" data-mp="9nixzh8n62fu">
                  {pick(platformInAction.organizations.headlineLead)}
                </span>
                <span className="block" data-id="element-5773" data-mp="4p7kmwj519sv">
                  {pick(platformInAction.organizations.headlineAccent)}
                </span>
              </h3>
              <ul className="mt-6 space-y-3" data-id="element-5774" data-mp="32h7ql4zpubk5">
                {platformInAction.organizations.items.map(item => {
                const Icon = ORG_ICONS[item.icon];
                return <li key={item.text.en} className="flex items-center gap-3" data-id="element-5775" data-mp="2tgjc696eu96q">
                      <IconBadge Icon={Icon} size="sm" data-id="element-5776" data-mp="2timbjjtgdeo4" />
                      <span className="font-sans text-sm text-[#121212]/80" data-id="element-5777" data-mp="2er3nqzucqfu7">
                        {pick(item.text)}
                      </span>
                    </li>;
              })}
              </ul>
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/bRyXP6hkH7M9i1qxoHZ5jj/Capture_d’ecran,_le_2026-06-22_a_08.50.11.png" alt={isFR ? 'Tableau de bord opérationnel BCITI — requêtes et performance' : 'BCITI operational dashboard — requests and performance'} className="hidden sm:block w-[46%] h-auto self-center object-cover object-left rounded-xl border border-[#E4E6EC] shadow-[0_6px_20px_rgba(18,18,18,0.08)]" data-id="element-5778" data-mp="25pdyo0nxzszl" />
          </motion.div>
        </div>

        {/* Conclusion */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="relative overflow-hidden rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-10 mt-4 text-center" data-id="element-5779" data-mp="1m5f0rtobptt8">
          <span className="pointer-events-none absolute -left-20 -bottom-20 w-72 h-72 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-5780" data-mp="1v6vby3mypzyi" />
          <span className="pointer-events-none absolute -right-20 -top-20 w-72 h-72 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-5781" data-mp="24wy4y3u33dwu" />
          <p className="relative font-serif text-2xl md:text-[2rem] leading-snug" data-id="element-5782" data-mp="2evnm1ue5ouc1">
            <span className="block text-[#121212]" data-id="element-5783" data-mp="2lybygusuphza">
              {pick(provenConclusion.lead)}
            </span>
            <span className="block text-[#4F46E5]" data-id="element-5784" data-mp="2qwvlyzn3f1tr">
              {pick(provenConclusion.accent)}
            </span>
          </p>
          <p className="relative font-sans text-[11px] tracking-[0.18em] uppercase text-[#5B6478] mt-5" data-id="element-5785" data-mp="2vtm21mywdxf6">
            {pick(provenConclusion.footer)}
          </p>
        </motion.div>

        {/* Case study */}
        <div className="text-center mt-10" data-id="element-5786" data-mp="39mv5l705zops">
          <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors duration-200" data-id="element-5787" data-mp="39lk4jey3sv2d">
            {isFR ? 'Explorer l’étude de cas de Brossard' : 'Explore the Brossard case study'}
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-5788" data-mp="1ka010odxax1c" />
          </Link>
        </div>
      </div>
    </section>;
}